import { useMutation } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ImageUploadOnPublishData } from '@xing-com/crate-entity-pages-common';
import {
  ExternalEditInfoBanner,
  ImageUpload,
  NotSavedDialog,
  SmallGrid,
} from '@xing-com/crate-entity-pages-common';
import {
  trackEditOpening,
  trackEditSaving,
} from '@xing-com/crate-entity-pages-common/tracking';
import {
  DialogContextProvider,
  useDialogContext,
} from '@xing-com/crate-entity-pages-entity-pages-shared-context-dialog-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useErrorContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { IconImage, IconUpload } from '@xing-com/icons';
import { Menu } from '@xing-com/menu';
import { ModalPage } from '@xing-com/modal-page';
import { usePopOver } from '@xing-com/pop-over';

import { EntityPageUpdateLogoDocument } from '../../../graphql/mutations/update-logo.gql-types';

import * as Styled from './edit-logo-menu.styles';
import { UpdateLogo } from './update-logo/update-logo';

type RenderEditLogoMenuProps = {
  onUploadLogo?: () => void;
};
const RenderEditLogoMenu: FC<RenderEditLogoMenuProps> = ({
  onUploadLogo = () => undefined,
}) => {
  const { locale, $t } = useIntl();
  const popOverMenu = usePopOver();
  const { showError } = useErrorContext();
  const { pageContext, setPageLogo } = usePageContext();
  const {
    setDataChanged,
    dataChanged,
    dialogConfirmation,
    setDialogConfirmation,
  } = useDialogContext();

  const [isEditCrop, setIsEditCrop] = useState<boolean>(false);
  const [showModalCover, setShowModalCover] = useState(false);

  const [updateLogo] = useMutation(EntityPageUpdateLogoDocument);

  const handleShowModalCover = () => setShowModalCover(true);
  const handleHideModalCover = () => setShowModalCover(false);

  const displayError = (error: any) => {
    showError({ message: 'EP_GENERAL_FEEDBACK_ERROR', error });
  };

  const handleOnClickMenuItem = (isEditCrop = false) => {
    trackEditOpening({
      focusType: pageContext.focusType,
      itemId: pageContext.pageId,
      module: 'header',
      part: 'logo',
    });

    setIsEditCrop(isEditCrop);
    popOverMenu.handleHide();
    handleShowModalCover();
  };

  const handleOnUpdateLogo = async ({
    cropped,
    original,
  }: ImageUploadOnPublishData) => {
    trackEditSaving({
      focusType: pageContext.focusType,
      itemId: pageContext.pageId,
      module: 'header',
      part: 'logo',
    });

    try {
      const { data, errors } = await updateLogo({
        variables: {
          id: pageContext.pageSlug,
          logoUploadId: cropped.id,
          originalLogoUploadId: original?.id,
        },
      });

      const error = errors ?? data?.entityPageUpdateLogo?.error;

      if (error) {
        displayError(error);
      } else {
        setPageLogo(URL.createObjectURL(cropped.file));
        setDataChanged(false);
        handleHideModalCover();
        onUploadLogo();
      }
    } catch (error) {
      displayError(error);
    }
  };

  const handleDiscard = () => {
    if (!dataChanged) {
      handleHideModalCover();
      return;
    }

    setDialogConfirmation({
      dialogAction: () => {
        setDialogConfirmation(null);
        setDataChanged(false);
        handleHideModalCover();
      },
    });
  };

  return (
    <>
      <Styled.LogoEditButton
        size="small"
        showIcon
        data-testid="EDIT_BUTTON_ON_LOGO"
        onClick={popOverMenu.handleShow}
        innerRef={popOverMenu.triggerRef}
      />

      <Menu
        dimmerTitle="close menu"
        onOutsideClick={popOverMenu.handleHide}
        triggerRef={popOverMenu.triggerRef}
        show={popOverMenu.show}
      >
        <Styled.ListWrapper>
          <Styled.ListItem onClick={() => handleOnClickMenuItem()}>
            <IconUpload width={24} height={24} />
            <Styled.Link noMargin size="small">
              <FormattedMessage id="EP_UPLOAD_LOGO" />
            </Styled.Link>
          </Styled.ListItem>
          {pageContext.logo ? (
            <Styled.ListItem onClick={() => handleOnClickMenuItem(true)}>
              <IconImage width={24} height={24} />
              <Styled.Link noMargin size="small">
                <FormattedMessage id="EP_EDIT_SECTION" />
              </Styled.Link>
            </Styled.ListItem>
          ) : null}
        </Styled.ListWrapper>
      </Menu>

      <ModalPage
        isShown={showModalCover}
        closeButton={{ onClick: handleHideModalCover }}
        isAnimated
        noGrid
      >
        <SmallGrid>
          {isEditCrop ? (
            <UpdateLogo
              onPublish={handleOnUpdateLogo}
              onDiscard={handleDiscard}
              onDragCrop={() => setDataChanged(true)}
              onCancel={handleHideModalCover}
            />
          ) : (
            <ImageUpload
              onDiscard={() => handleDiscard()}
              onFileSelected={() => setDataChanged(true)}
              shouldUploadOriginal={true}
              onPublish={handleOnUpdateLogo}
              onCancel={handleHideModalCover}
              imageUploadCopy={{
                headline: $t({ id: 'EP_CHOOSE_LOGO' }),
                hint: $t({ id: 'EP_UPLOAD_LOGO_HINT' }),
                label: $t({ id: 'EP_UPLOAD_IMAGE_LABEL' }),
                onDragLabel: $t({ id: 'EP_UPLOAD_IMAGE_DRAG_LABEL' }),
              }}
            />
          )}
          <ExternalEditInfoBanner
            copyKey="EP_UPLOAD_LOGO_EDIT_INFO"
            ctaUrl={`https://faq.xing.com/${locale}/node/68249`}
            ctaCopyKey="EP_UPLOAD_LOGO_EDIT_INFO_CTA"
          />
        </SmallGrid>
      </ModalPage>
      {dataChanged && dialogConfirmation?.dialogAction && <NotSavedDialog />}
    </>
  );
};

export const EditLogoMenu: FC<RenderEditLogoMenuProps> = (props) => (
  <DialogContextProvider>
    <RenderEditLogoMenu {...props} />
  </DialogContextProvider>
);
